import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactDropdown from 'shared/design-system/components/TrueImpactDropdown';
import TrueImpactIconButton from 'shared/design-system/components/TrueImpactIconButton';
import {
  faPen,
  faFloppyDisk,
  faSpinnerThird,
} from '@fortawesome/pro-light-svg-icons';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { NPOUser } from '../NewProgram/api';
import { RequestState } from 'shared/auth/hooks/useRequestState';
import { Program } from '../../Reports/NewReportDialog';
import { TrueImpactButtonWithLoading } from 'shared/design-system/components/TrueImpactButton';
import { updateProgram } from 'shared/components/WelcomeBack/api';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import { SuccessResponse } from '../NewReport/api';
import { useClient } from 'shared/apollo';
import { useUserActions } from 'shared/auth/hooks/useUserActions';
import OrgNameWarningModal, {
  matchesOrgName,
} from '../LevelOneFeedback/ProgramNameWarningModal';
import { useFeatureFlags } from 'shared/providers/FeatureFlags/useFeatureFlags';
import ProgramQuestionsModal from '../ProgramQuestions/ProgramQuestionsModal';
import ProgramNameDonorField from '../ProgramDonorName/ProgramNameDonorField';
import ProgramDonorNameModal from '../ProgramDonorName/ProgramDonorNameModal';
import { useProgramNameDonorFlag } from '../ProgramDonorName/useProgramNameDonorFlag';
import ContributionEligibilitySelector, {
  ContributionEligibility,
} from '../ContributionEligibility/ContributionEligibilitySelector';

export interface ProgramColumnProps {
  programName: string;
  allNPOUsers: RequestState<{
    allUsers: NPOUser[];
  }>;
  program: Program;
  programList: Program[];
  reloadProgramList: () => unknown;
}

const StyledDiv = styled.div`
  .program-edit-row {
    display: flex;
    justify-content: space-between;
  }
  .text-label {
    margin-top: ${SPACERS.tiny};
    margin-bottom: ${SPACERS.tiny};
  }
  .dropdown-select {
    margin-bottom: ${SPACERS.small};
  }
  .save-button {
    display: inline-block;
  }
  .profiling-button {
    display: inline-block;
    margin-top: ${SPACERS.small};
  }
  .eligibility-field {
    margin-top: ${SPACERS.small};
    margin-bottom: ${SPACERS.small};
  }
`;

const ProgramColumn: React.FC<ProgramColumnProps> = ({
  program,
  allNPOUsers,
  programName,
  programList,
  reloadProgramList,
}) => {
  const client = useClient();
  const { organizationName } = useUserActions();
  const levelOneFeedbackEnabled = useFeatureFlags('levelOneFeedback');
  const programProfilingEnabled = useFeatureFlags('programProfiling');
  const [errorText, setErrorText] = useState<string | undefined>(undefined);
  const [showOrgNameWarning, setShowOrgNameWarning] = useState(false);
  const [pendingProgramName, setPendingProgramName] = useState<string | null>(
    null,
  );
  const [edit, setEdit] = useState<boolean>(false);
  const [editName, setEditName] = useState<string>(programName);
  const donor = useProgramNameDonorFlag(
    edit ? editName : '',
    program.level1DonorName ? programName : null,
  );
  const submitPendingRef = useRef(false);
  const [showProfiling, setShowProfiling] = useState<boolean>(false);
  const [requestState, makeRequest] = useGraphQLRequest<
    SuccessResponse<'updateProgram'>
  >();

  const handleProfilingOpen = useCallback(() => setShowProfiling(true), []);
  const handleProfilingClose = useCallback(() => {
    setShowProfiling(false);
    reloadProgramList();
  }, [reloadProgramList]);
  const [manager, setManager] = useState<NPOUser | undefined>(
    allNPOUsers.data?.allUsers.find(u => u.userId === program.managerId),
  );
  const [eligibility, setEligibility] = useState<
    ContributionEligibility | null | undefined
  >(program.contributionEligibility ?? undefined);
  const [eligibilitySaving, setEligibilitySaving] = useState(false);

  const handleEligibilityChange = useCallback(
    (value: ContributionEligibility | null) => {
      if (!client || !manager || value === eligibility) return;
      // Contribution eligibility is required on save; ignore transitions to
      // null (e.g. unchecking both Donations & Volunteers). Users must pick
      // Donations, Volunteers, Both, or the explicit "None" option.
      if (value == null) return;
      const previous = eligibility;
      setEligibility(value);
      setEligibilitySaving(true);
      Promise.resolve(
        updateProgram(
          client,
          program.Id,
          manager.userId,
          undefined,
          undefined,
          value ?? undefined,
        ),
      )
        .catch(() => {
          setEligibility(previous);
        })
        .finally(() => {
          setEligibilitySaving(false);
        });
    },
    [client, manager, program.Id, eligibility],
  );

  useEffect(() => {
    if (requestState.type === 'REQUEST_SUCCESS') {
      setEdit(false);
      reloadProgramList();
    }
  }, [requestState.type, reloadProgramList, setEdit]);

  const handleSave = useCallback(() => {
    if (!manager || !client) return;
    makeRequest(updateProgram(client, program.Id, manager.userId));
  }, [client, manager, program.Id]);

  const handleEdit = useCallback(async (donorNameResolved = false) => {
    const value = editName;
    if (edit && value === programName) {
      setEdit(false);
      return;
    }
    if (edit && manager && client && programName !== value) {
      if (!value) {
        setErrorText('Please enter a valid program name');
        return;
      }
      if (programList.findIndex(x => x.name === value) !== -1) {
        setErrorText('Program name already exists');
        return;
      }
      setErrorText(undefined);
      // The backend re-validates and forces PRIVATE; this only asks the user first.
      if (!donorNameResolved && (await donor.flushCheck())) {
        submitPendingRef.current = true;
        donor.openModal();
        return;
      }
      if (levelOneFeedbackEnabled && matchesOrgName(value, organizationName)) {
        setPendingProgramName(value);
        setShowOrgNameWarning(true);
        return;
      }
      makeRequest(
        updateProgram(
          client,
          program.Id,
          manager.userId,
          value,
          program.level1Note,
        ),
      );
      return;
    }
    setEditName(programName);
    setEdit(true);
  }, [
    edit,
    editName,
    donor,
    programName,
    client,
    manager,
    programList,
    setEdit,
    setErrorText,
    organizationName,
    levelOneFeedbackEnabled,
  ]);

  const handleConfirmedUpdate = useCallback(
    (level1Note: string) => {
      if (!pendingProgramName || !manager || !client) return;
      setShowOrgNameWarning(false);
      makeRequest(
        updateProgram(
          client,
          program.Id,
          manager.userId,
          pendingProgramName,
          level1Note,
        ),
      );
    },
    [client, manager, program.Id, pendingProgramName, makeRequest],
  );

  const handleKeepName = useCallback(() => {
    donor.keepName();
    if (submitPendingRef.current) {
      submitPendingRef.current = false;
      handleEdit(true);
    }
  }, [donor.keepName, handleEdit]);

  const handleCorrectName = useCallback(() => {
    submitPendingRef.current = false;
    donor.closeModal();
  }, [donor.closeModal]);

  const users = allNPOUsers.data?.allUsers || [];

  const isLoading = requestState.type === 'REQUEST_START';
  return (
    <>
      <OrgNameWarningModal
        open={showOrgNameWarning}
        isMatchedDonorName={false}
        programName={pendingProgramName}
        onConfirm={handleConfirmedUpdate}
        onClose={() => setShowOrgNameWarning(false)}
      />
      <ProgramDonorNameModal
        open={donor.modalOpen}
        onKeepName={handleKeepName}
        onCorrectName={handleCorrectName}
      />
      <StyledDiv>
        <div className="program-edit-row">
          <TrueImpactText variant="heading-label" color="blackBase">
            Program
          </TrueImpactText>
          <TrueImpactIconButton
            icon={isLoading ? faSpinnerThird : edit ? faFloppyDisk : faPen}
            spin={isLoading}
            label={edit ? 'Save' : 'Edit'}
            onClick={() => handleEdit()}
          />
        </div>
        {edit ? (
          <>
            <ProgramNameDonorField
              value={editName}
              donor={donor}
              onChange={setEditName}
              placeholder="Program Name"
            />
            {errorText && (
              <TrueImpactText variant="body-small" color="critical">
                {errorText}
              </TrueImpactText>
            )}
          </>
        ) : (
          <TrueImpactText variant="heading-page">{programName}</TrueImpactText>
        )}
        <TrueImpactText variant="body-bold" className="text-label">
          Reporting Manager
        </TrueImpactText>
        <TrueImpactDropdown
          value={manager}
          options={users}
          getId={p => p.userId}
          getLabel={p => `${p.firstName} ${p.lastName} (${p.email})`}
          native={true}
          onChange={setManager}
          onClear={undefined}
          getItemDisability={undefined}
          name="Please select program manager"
          className="dropdown-select"
        />
        {manager && manager.userId !== program.managerId && (
          <TrueImpactButtonWithLoading
            isLoading={requestState.type === 'REQUEST_START'}
            onClick={handleSave}
            className="save-button"
          >
            Save
          </TrueImpactButtonWithLoading>
        )}
        {programProfilingEnabled && program.isCompleted && (
          <TrueImpactButtonWithLoading
            variant="secondary-large"
            onClick={handleProfilingOpen}
            className="profiling-button"
          >
            Edit Program Profiles
          </TrueImpactButtonWithLoading>
        )}
      </StyledDiv>
      <ProgramQuestionsModal
        open={showProfiling}
        programId={program.Id}
        onClose={handleProfilingClose}
      />
    </>
  );
};

export default ProgramColumn;
